/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { Col, Row } from "react-awesome-styled-grid";
import { SubmitHandler, useForm, FormProvider } from "react-hook-form";
import { z } from "zod";
import { Input, Label } from "@nordcloud/gnui";
import { FormGroup } from "~/components";
import { safeStr, stringRequired } from "~/components/Forms";
import { SelectUsersSidebar } from "~/views/applications/components/SelectUsersSidebar";
import {
  MAX_DISPLAY_NAME_LENGTH,
  MAX_DESCRIPTION_LENGTH,
} from "~/views/applications/constants";
import { useApplicationWizard } from "./ApplicationProvider";
import { ApplicationData } from "./types";

type Props = {
  nextStep: () => void;
};

export const DetailsSchema = z.object({
  name: stringRequired("name")
    .max(MAX_DISPLAY_NAME_LENGTH, {
      message: `Application Name is too long (max: ${MAX_DISPLAY_NAME_LENGTH} characters)`,
    })
    .refine((val) => val.trim() !== "", {
      message: "name cannot be only spaces",
    }),
  owner: stringRequired("owner"),
  description: safeStr
    .max(MAX_DESCRIPTION_LENGTH, {
      message: `Description is too long (max: ${MAX_DESCRIPTION_LENGTH} characters)`,
    })
    .optional()
    .nullable()
    .refine(
      (val) => {
        const descriptionLength = val?.length ?? 0;

        if (descriptionLength > 0 && val?.trim() === "") {
          return false;
        }

        return true;
      },
      {
        message: "description cannot be only spaces",
      }
    ),
});

export function ApplicationDetailsForm({ nextStep }: Props) {
  const { applicationData, setApplicationData } = useApplicationWizard();

  const formMethods = useForm({
    resolver: zodResolver(DetailsSchema),
    defaultValues: {
      name: applicationData?.name,
      description: applicationData?.description,
      owner: applicationData?.owner,
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = formMethods;

  const submit: SubmitHandler<Partial<ApplicationData>> = (formData) => {
    setApplicationData((prevApplicationData: ApplicationData) => ({
      ...prevApplicationData,
      ...formData,
    }));

    nextStep();
  };

  return (
    <Row justify="center">
      <Col sm={6} md={6} lg={8}>
        <Row>
          <Col>
            <FormProvider {...formMethods}>
              <form id="applicationDetailsForm" onSubmit={handleSubmit(submit)}>
                <FormGroup error={errors.name}>
                  <Label name="Application Name" required />
                  <Input
                    id="name"
                    {...register("name")}
                    placeholder="Application Name"
                  />
                </FormGroup>
                <FormGroup error={errors.owner}>
                  <Label name="Application Owner" htmlFor="owner" required />
                  <SelectUsersSidebar
                    id="owner"
                    placeholder="Select Application Owner"
                    sidebarTitle="Select Application Owner"
                    inputValue={applicationData?.owner ?? ""}
                    isLoading={false}
                    onSelect={(subject) => {
                      setApplicationData(
                        (prevApplicationData: ApplicationData) => ({
                          ...prevApplicationData,
                          owner: subject,
                        })
                      );
                      setValue("owner", subject);
                    }}
                    isError={errors?.owner && "danger"}
                  />
                </FormGroup>
                <FormGroup>
                  <Label name="Contact Person" htmlFor="contact" />
                  <SelectUsersSidebar
                    id="contact"
                    placeholder="Select Contact Person"
                    sidebarTitle="Select Contact Person"
                    inputValue={applicationData?.contactPerson ?? ""}
                    isLoading={false}
                    onSelect={(subject) => {
                      setApplicationData(
                        (prevApplicationData: ApplicationData) => ({
                          ...prevApplicationData,
                          contactPerson: subject,
                        })
                      );
                    }}
                  />
                </FormGroup>
                <FormGroup error={errors["description"]}>
                  <Label name="Description" htmlFor="description" />
                  <Input
                    id="description"
                    {...register("description")}
                    placeholder="Provide Application Description"
                  />
                </FormGroup>
              </form>
            </FormProvider>
          </Col>
        </Row>
      </Col>
    </Row>
  );
}
